import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import clinicBooking from '../../../../../services/clinicbooking.service';
import { useParams } from 'react-router-dom';
import DocInfo from './DocInfo';
import Loading from "../../../../common/Loading";
import { useForm } from "react-hook-form";
import clinicService from "../../../../../services/clinic.service";
import clinicbookingService from "../../../../../services/clinicbooking.service";

// Add the AddDoctorInfo component here
function AddDoctorInfo({ onDoctorAdded }) {
    const [clinicDoctors, setClinicDoctors] = useState([]);
    const [existingBookingDoctors, setExistingBookingDoctors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { clinicId } = useParams();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm();

    useEffect(() => {
        Promise.all([getClinicDoctors(), getExistingBookingDoctors()])
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const getClinicDoctors = () => {
        return clinicService.getUsers(clinicId, "ECP")
            .then((res) => {
                setClinicDoctors(res.data);
            })
            .catch((err) => {
                console.log(err);
                toast.error("Failed to load doctors", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            });
    };

    const getExistingBookingDoctors = () => {
        return clinicbookingService.getAllDoctors(clinicId)
            .then((res) => {
                setExistingBookingDoctors(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getAvailableDoctors = () => {
        const existingDoctorIds = existingBookingDoctors.map(doc => doc.userId);
        return clinicDoctors.filter(doc => !existingDoctorIds.includes(doc.id));
    };

    const onSubmit = (data) => {
        setIsSubmitting(true);
        const newData = { ...data, clinicId: clinicId };

        clinicbookingService.addUpdateDoctor(clinicId, newData)
            .then((res) => {
                toast.success("Doctor information saved successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });

                reset();
                getExistingBookingDoctors();

                if (onDoctorAdded) {
                    onDoctorAdded();
                }
            })
            .catch((err) => {
                console.log(err);
                toast.error(err.response?.data?.message || "Failed to save doctor information", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const availableDoctors = getAvailableDoctors();

    return (
        <div className="card card-border shadow-none">
            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="userId" className="form-label small">
                                    Select Doctor <span className="text-danger">*</span>
                                </label>
                                {isLoading ? (
                                    <div className="d-flex align-items-center">
                                        <Loading />
                                    </div>
                                ) : availableDoctors.length === 0 ? (
                                    <div className="alert alert-warning py-2 mb-0">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        All doctors are already added to the booking system.
                                    </div>
                                ) : (
                                    <>
                                        <select
                                            className={`form-select ${errors.userId ? 'is-invalid' : ''}`}
                                            {...register("userId", { required: "Doctor selection is required" })}
                                        >
                                            <option value="">Select a doctor</option>
                                            {availableDoctors.map((doc) => (
                                                <option key={doc.id} value={doc.id}>
                                                    Dr. {doc.firstName} {doc.lastName}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.userId && (
                                            <div className="invalid-feedback">{errors.userId.message}</div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="name" className="form-label small">
                                    Display Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    {...register("name", { required: "Display name is required" })}
                                    placeholder="Enter display name"
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">{errors.name.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="photoUrl" className="form-label small">
                                    Photo URL
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-image"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        {...register("photoUrl")}
                                        placeholder="https://example.com/photo.jpg"
                                    />
                                </div>
                                <small className="text-muted">Leave blank to use default avatar</small>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="isActive" className="form-label small">
                                    Status <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.isActive ? 'is-invalid' : ''}`}
                                    {...register("isActive", { required: "Status is required" })}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                                {errors.isActive && (
                                    <div className="invalid-feedback">{errors.isActive.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="form-group">
                                <label htmlFor="bio" className="form-label small">
                                    Biography
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    {...register("bio")}
                                    placeholder="Enter doctor's biography (education, specialties, etc.)"
                                ></textarea>
                            </div>
                        </div>

                        <div className="col-12 mt-4">
                            <div className="d-flex justify-content-end gap-2">
                                <button
                                    type="submit"
                                    className="btn btn-success btn-sm px-4"
                                    disabled={isSubmitting || availableDoctors.length === 0}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-save me-2"></i>
                                            Save Doctor
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Main AllDocs component
function AllDocs({ refresh = false }) {
    const [docList, setDocList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDoc, setSelectedDoc] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [showAddForm, setShowAddForm] = useState(false);
    const { clinicId } = useParams();

    useEffect(() => {
        getAllDoctors();
    }, [refresh, showAddForm]); // Add showAddForm to dependency array

    const getAllDoctors = () => {
        setIsLoading(true);
        clinicBooking.getAllDoctors(clinicId)
            .then(res => {
                setDocList(res.data);
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleEdit = (doc) => {
        setSelectedDoc(doc);
        setIsEditing(true);
    };

    const handleBackToList = () => {
        setIsEditing(false);
        getAllDoctors();
    };

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleAddForm = () => {
        setShowAddForm(!showAddForm);
    };

    const handleDoctorAdded = () => {
        setShowAddForm(false);
        getAllDoctors();
    };

    // Calculate stats for the dashboard
    const activeDoctors = docList.filter(doc => doc.isActive).length;
    const inactiveDoctors = docList.filter(doc => !doc.isActive).length;
    const totalDoctors = docList.length;

    if (isEditing) {
        return <DocInfo doc={selectedDoc} onBack={handleBackToList} />;
    }

    return (
        <div className="container-fluid py-3">
            {/* Stats Summary with Add Doctor button */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted">
                            <span className="badge bg-success me-2">Active: {activeDoctors}</span>
                            <span className="badge bg-danger me-2">Inactive: {inactiveDoctors}</span>
                            <span className="badge bg-primary">Total: {totalDoctors}</span>
                        </div>
                        <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={toggleAddForm}
                        >
                            <i className="bi bi-plus-circle me-1"></i>
                            Add New Doctor
                        </button>
                    </div>
                </div>
            </div>

            {/* Conditionally render Add Doctor form or Doctors Table */}
            {showAddForm ? (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>Add New Doctor</h5>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={toggleAddForm}
                            >
                                <i className="bi bi-arrow-left me-1"></i>
                                Back to List
                            </button>
                        </div>
                        <AddDoctorInfo onDoctorAdded={handleDoctorAdded} />
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-none">
                            <div className="card-body p-0">
                                {isLoading ? (
                                    <div className="text-center py-5">
                                        <Loading />
                                    </div>
                                ) : docList.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped table-hover align-middle mb-0">
                                            <thead>
                                            <tr>
                                                <th width="10%">
                                                    <i className="bi bi-arrows-expand me-1"></i>
                                                    Expand
                                                </th>
                                                <th width="10%">
                                                    <i className="bi bi-image me-1"></i>
                                                    Photo
                                                </th>
                                                <th width="25%">
                                                    <i className="bi bi-person-badge me-1"></i>
                                                    Doctor Name
                                                </th>
                                                <th width="20%">
                                                    <i className="bi bi-calendar-plus me-1"></i>
                                                    Joined Date
                                                </th>
                                                <th width="10%">
                                                    <i className="bi bi-circle-fill me-1"></i>
                                                    Status
                                                </th>
                                                <th width="15%" className="text-center">
                                                    <i className="bi bi-gear me-1"></i>
                                                    Actions
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {docList.map((doc) => (
                                                <React.Fragment key={doc.id}>
                                                    <tr>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-link"
                                                                onClick={() => toggleRowExpansion(doc.id)}
                                                            >
                                                                <i className={`bi ${expandedRows[doc.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                                            </button>
                                                        </td>
                                                        <td>
                                                            {doc.photoUrl ? (
                                                                <img
                                                                    src={doc.photoUrl}
                                                                    alt={doc.name}
                                                                    className="rounded-circle"
                                                                    width="40"
                                                                    height="40"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : <>
                                                                <div
                                                                    className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                                                                    style={doc.photoUrl ? {display: 'none', width: '40px', height: '40px'} : {display: 'flex', width: '40px', height: '40px'}}
                                                                >
                                                                    {doc.name.charAt(0)}
                                                                </div>
                                                            </>}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span className="fw-medium">{doc.name}</span>
                                                                <small className="text-muted">{doc.email}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted">{doc.createdAt}</span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge small service-status ${doc.isActive ? 'status-completed text-success' : 'status-incomplete text-danger'}`}>
                                                                {doc.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => handleEdit(doc)}
                                                                    title="Edit doctor"
                                                                >
                                                                    <i className="bi bi-pencil"></i> Edit
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {expandedRows[doc.id] && (
                                                        <tr>
                                                            <td colSpan="7" className="p-3 bg-light">
                                                                <div className="row">
                                                                    <div className="col-md-2 text-center">
                                                                        {doc.photoUrl ? (
                                                                            <img
                                                                                src={doc.photoUrl}
                                                                                alt={doc.name}
                                                                                className="rounded-circle"
                                                                                width="80"
                                                                                height="80"
                                                                            />
                                                                        ) : (
                                                                            <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white mx-auto"
                                                                                 style={{width: '80px', height: '80px', fontSize: '1.5rem'}}>
                                                                                {doc.name.charAt(0)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="col-md-10">
                                                                        <h6>About Dr. {doc.name}</h6>
                                                                        <p className="text-muted mb-2">
                                                                            {doc.bio || 'No biography available for this doctor.'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5 card-border">
                                        <i className="bi bi-person-plus fs-1 text-muted"></i>
                                        <h4 className="mt-3">No Doctors Found</h4>
                                        <p className="mb-4">Get started by adding doctors to your clinic.</p>
                                        <button
                                            className="btn btn-primary"
                                            onClick={toggleAddForm}
                                        >
                                            <i className="bi bi-plus-circle me-1"></i>
                                            Add First Doctor
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AllDocs;